import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { styled } from 'styled-components';

import { MdDelete } from 'react-icons/md';
import { MdModeEdit } from 'react-icons/md';
import { FaPlus } from 'react-icons/fa';

import { createIssueType, deleteIssueType, getIssueTypes } from '../services/IssueTypesService';
import { useAppContext } from '../contextApi/context';
import { ISSUE_TYPE_DELETE_ASSIGNED, ISSUE_TYPE_NAME_DUPLICATED } from '../constants/apiMessages';
import DeleteConfirmationModal from '../components/shared/DeleteConfirmationModal';
import ConfirmationModal from '../components/shared/ConfirmationModal';
import { AddButton, TableActionButton, ToggleOption } from '../styles/StyledComponents';
import { checkPermission } from '../utils/permissions';
import TableContainer from '../components/table/TableContainer';

const IssueTypes = () => {
    // ----- Context/Hooks
    const { loggedUser } = useAppContext();

    // ----- State
    const [idToDelete, setIdToDelete] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [showIssueTypeCreated, setShowIssueTypeCreated] = useState(false);
    const [showIssueTypeDeleted, setShowIssueTypeDeleted] = useState(false);
    const [showErrorDuplicatedIssueType, setShowErrorDuplicatedIssueType] = useState(false);
    const [showErrorAssignedIssueType, setShowErrorAssignedIssueType] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [updateTable, setUpdateTable] = useState(false);
    const [vehicleRequired, setVehicleRequired] = useState(true);

    // ----- Hooks
    const {
        register,
        reset,
        handleSubmit,
        formState: { errors },
    } = useForm();

    // ----- Actions
    const handleCloseDeleteConfirmationModal = () => {
        setIdToDelete(null);
        setShowDeleteConfirmation(false);
    };

    const openDeleteConfirmation = (roleId) => {
        setIdToDelete(roleId);
        setShowDeleteConfirmation(true);
    };

    const handleCreate = async (data) => {
        clearMessages();

        const formData = {
            ...data,
            vehicleRequired,
            requesterRequired: true,
        };

        const response = await createIssueType(loggedUser.token, formData);

        if (response.status === 201) {
            setShowIssueTypeCreated(true);
            setUpdateTable(!updateTable);
            handleResetForm();
        } else {
            // Error from API
            const { data } = response;

            if (data.includes(ISSUE_TYPE_NAME_DUPLICATED)) {
                setShowErrorDuplicatedIssueType(true);
            }
        }
    };

    const handleShowForm = () => {
        setShowForm(!showForm);
    };

    const handleVehicleRequiredChange = (e) => {
        const isVehicleRequired = e.target.value === 'true';
        setVehicleRequired(isVehicleRequired);
    };

    const handleDelete = async () => {
        clearMessages();
        const response = await deleteIssueType(loggedUser.token, idToDelete);

        if (response.status === 204) {
            setShowDeleteConfirmation(false);
            setUpdateTable(!updateTable);
            setShowIssueTypeDeleted(true);
        } else {
            // Error from API
            const { data } = response;

            if (data === ISSUE_TYPE_DELETE_ASSIGNED) {
                setShowDeleteConfirmation(false);
                setShowErrorAssignedIssueType(true);
            }
        }
    };

    const handleShowConfirmationModal = () => {
        setShowConfirmationModal(true);
    };

    const handleCloseConfirmationModal = () => {
        setShowConfirmationModal(false);
    };

    const handleResetForm = () => {
        reset();
        setVehicleRequired(true);
        setShowForm(false);
        setShowConfirmationModal(false);
    };

    const clearMessages = () => {
        setShowIssueTypeCreated(false);
        setShowErrorDuplicatedIssueType(false);
        setShowIssueTypeDeleted(false);
        setShowErrorAssignedIssueType(false);
    };

    const Actions = ({ row }) => (
        <div className='d-flex'>
            {checkPermission(loggedUser.permissions, 'edit_issue_types') && (
                <TableActionButton className='btn btn-primary'>
                    <Link to={`/tipos-despacho/${row.id}`}>
                        <MdModeEdit /> Editar
                    </Link>
                </TableActionButton>
            )}
            {checkPermission(loggedUser.permissions, 'delete_issue_types') && (
                <TableActionButton className='btn btn-danger' onClick={() => openDeleteConfirmation(row.id)}>
                    <MdDelete />
                    Eliminar
                </TableActionButton>
            )}
        </div>
    );

    const columns = [
        {
            name: 'Nombre',
            selector: (row) => row.name,
            sortable: true,
            sortField: 'name',
        },
        {
            name: 'Descripción',
            selector: (row) => row.description,
        },
        {
            name: 'Vehículo requerido',
            selector: (row) => (row.vehicleRequired ? 'Si' : 'No'),
            sortable: true,
            sortField: 'vehicleRequired',
        },
        {
            name: 'Acciones',
            width: '215px',
            cell: (row) => <Actions row={row} />,
        },
    ];

    const filters = [
        {
            column: 'name',
            input: 'text',
            label: 'Nombre',
            operation: 'like',
            length: 50,
        },
        {
            column: 'vehicleRequired',
            input: 'select',
            label: 'Vehículo requerido',
            options: [
                { value: 'true', text: 'Si' },
                { value: 'false', text: 'No' },
            ],
            operation: 'equal',
        },
    ];

    // ----- Render
    return (
        <div className='container mt-5'>
            {showIssueTypeCreated && (
                <div className='alert alert-success text-center mb-1' role='alert'>
                    Tipo de despacho creado satisfactoriamente.
                </div>
            )}

            {showIssueTypeDeleted && (
                <div className='alert alert-success text-center mb-1' role='alert'>
                    Tipo de despacho eliminado satisfactoriamente.
                </div>
            )}

            {showErrorDuplicatedIssueType && (
                <div className='alert alert-danger text-center mb-1' role='alert'>
                    El nombre del tipo de despacho ya se encuentra registrado.
                </div>
            )}

            {showErrorAssignedIssueType && (
                <div className='alert alert-danger text-center mb-1' role='alert'>
                    El tipo de despacho no se puede eliminar porque está asignado a un despacho.
                </div>
            )}

            <h1>Tipos de Despacho</h1>

            <div className='d-flex mt-3 justify-content-end'>
                {checkPermission(loggedUser.permissions, 'add_issue_types') && (
                    <AddButton className='btn btn-primary' onClick={handleShowForm}>
                        <FaPlus /> Agregar nuevo
                    </AddButton>
                )}
            </div>

            {showForm && (
                <FormContainer>
                    <form onSubmit={handleSubmit(handleCreate)}>
                        <label className='d-flex mb-1' htmlFor='name'>
                            Nombre
                        </label>
                        <input
                            className='form-control'
                            type='text'
                            id='name'
                            autoComplete='off'
                            maxLength={50}
                            {...register('name', { required: true, maxLength: 50 })}
                            autoFocus
                        />
                        <span className='my-1' role='alert'>
                            {errors.name?.type === 'required' && `Campo requerido`}
                            {errors.name?.type === 'maxLength' && `Formato inválido`}
                        </span>

                        <label className='d-flex mb-1' htmlFor='description'>
                            Descripción
                        </label>
                        <textarea
                            className='form-control'
                            id='description'
                            maxLength={100}
                            rows='3'
                            {...register('description', { required: true, maxLength: 100 })}
                        />
                        <span className='my-1' role='alert'>
                            {errors.description?.type === 'required' && `Campo requerido`}
                            {errors.description?.type === 'maxLength' && `Formato inválido`}
                        </span>

                        <label className='d-flex mb-1'>Vehículo requerido</label>
                        <ToggleOption className='mb-3'>
                            <input
                                type='radio'
                                className='btn-check'
                                name='vehicleRequired'
                                id='vehicleYes'
                                value='true'
                                onChange={handleVehicleRequiredChange}
                                checked={vehicleRequired === true}
                                defaultChecked
                            />
                            <label className='btn btn-outline-success first-option' htmlFor='vehicleYes'>
                                Si
                            </label>

                            <input
                                type='radio'
                                className='btn-check'
                                name='vehicleRequired'
                                id='vehicleNo'
                                value='false'
                                onChange={handleVehicleRequiredChange}
                                checked={vehicleRequired === false}
                            />
                            <label className='btn btn-outline-success second-option' htmlFor='vehicleNo'>
                                No
                            </label>
                        </ToggleOption>

                        <div className='d-flex justify-content-center mt-3'>
                            <button className='btn btn-primary reset' type='button' onClick={handleShowConfirmationModal}>
                                Cancelar
                            </button>
                            <button className='btn btn-danger' type='submit'>
                                Guardar
                            </button>
                        </div>
                    </form>
                </FormContainer>
            )}

            <TableContainer columns={columns} fetchDataHandler={getIssueTypes} updateTable={updateTable} filters={filters} />

            {showDeleteConfirmation && (
                <DeleteConfirmationModal
                    text={'Desea eliminar el tipo de despacho?'}
                    closeHandler={handleCloseDeleteConfirmationModal}
                    actionHandler={handleDelete}
                    show={showDeleteConfirmation}
                />
            )}

            {showConfirmationModal && (
                <ConfirmationModal closeHandler={handleCloseConfirmationModal} actionHandler={handleResetForm} show={showConfirmationModal} />
            )}
        </div>
    );
};

const FormContainer = styled.div`
    border: #dee2e6 solid 1px;
    margin: 25px auto;
    padding: 40px;
    width: 60%;

    form {
        display: grid;

        > label {
            font-size: 15px;
            font-weight: bold;
        }

        > span {
            color: #d04437;
            font-size: 13px;
            min-height: 12px;
        }

        div > button {
            &.reset {
                background-color: #a5a7a0;
                border-color: #a5a7a0;
                margin-right: 20px;

                &:active {
                    background-color: #a5a7a0;
                    border-color: #a5a7a0;
                }
            }
        }

        > textarea {
            resize: none;
        }
    }
`;

export default IssueTypes;
